import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  fontStacks,
  FormControlLabel,
  HairlineCard,
  IconButton,
  Icons,
  iconSize,
  InputAdornment,
  MenuItem,
  palette,
  quietScrollbar,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useSnackbar,
  visuallyHidden,
} from '@munifocus/ui';
import {
  CONTACT_CHANNELS,
  CONTACT_NOTES_MAX,
  CONTACT_TOPICS,
  PICKABLE_CONTACT_OUTCOMES,
  intakeHrefForContact,
  type ContactChannel,
  type ContactLinkedFile,
  type ContactLogEntryView,
  type ContactTopic,
  type CreateContactLogDto,
  type PickableContactOutcome,
} from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { PageIntro } from '../components/PageIntro';
import { CallbackFields } from '../components/front-desk/CallbackFields';
import { ContactEntrySummary } from '../components/front-desk/ContactEntrySummary';
import { FileLinkPicker } from '../components/front-desk/FileLinkPicker';
import { useAuth } from '../providers/AuthProvider';
import {
  defaultCallbackDue,
  localInputToIso,
  useAttachContact,
  useContactAssignees,
  useContactEntries,
  useCreateContactEntry,
  useFrontDeskStatus,
  useSendContactToTriage,
  useSetContactCallback,
} from '../lib/front-desk-api';

interface FormState {
  channel: ContactChannel;
  anonymous: boolean;
  callerName: string;
  callerPhone: string;
  callerEmail: string;
  address: string;
  pid: string;
  ownerName: string;
  topic: ContactTopic;
  notes: string;
  /** Empty until the clerk picks one: logging "Resolved" by default hid every callback. */
  outcome: PickableContactOutcome | '';
  files: ContactLinkedFile[];
  callbackAssigneeId: string;
  callbackDue: string;
}

function emptyForm(): FormState {
  return {
    channel: 'phone',
    anonymous: false,
    callerName: '',
    callerPhone: '',
    callerEmail: '',
    address: '',
    pid: '',
    ownerName: '',
    topic: 'new_complaint',
    notes: '',
    outcome: '',
    files: [],
    callbackAssigneeId: '',
    callbackDue: defaultCallbackDue(),
  };
}

const CHANNEL_ICON: Record<ContactChannel, typeof Icons.Phone> = {
  phone: Icons.Phone,
  walk_in: Icons.User,
  email: Icons.Mail,
};

/** A numbered step of the log form: the order is the order of the conversation. */
function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  const id = useId();
  return (
    <Box
      component="section"
      aria-labelledby={id}
      sx={{ py: 2.5, borderTop: `1px solid ${palette.border.line}` }}
    >
      <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', mb: 1.5 }}>
        <Box
          aria-hidden
          sx={{
            width: 22,
            height: 22,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 700,
            backgroundColor: palette.brand.accentTint,
            color: palette.brand.accent,
          }}
        >
          {n}
        </Box>
        <Typography
          id={id}
          component="h3"
          sx={{ fontFamily: fontStacks.display, fontWeight: 700, fontSize: 15 }}
        >
          {title}
        </Typography>
      </Stack>
      {children}
    </Box>
  );
}

/**
 * One outcome, as a radio card: the name, and one line on what logging it
 * will do — the question the old dropdown left every clerk to guess at.
 */
function OutcomeOption({
  value,
  checked,
  onChange,
  title,
  effect,
  children,
}: {
  value: PickableContactOutcome;
  checked: boolean;
  onChange: (value: PickableContactOutcome) => void;
  title: string;
  effect: string;
  children?: ReactNode;
}) {
  return (
    <Box
      sx={{
        border: `1px solid ${checked ? palette.brand.accent : palette.border.interactive}`,
        backgroundColor: checked ? palette.brand.accentTint : palette.background.paper,
        borderRadius: 1.5,
        transition: 'border-color 200ms, background-color 200ms',
        '&:hover': { borderColor: palette.brand.accent },
        '&:has(input:focus-visible)': {
          outline: `2px solid ${palette.brand.accent}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        component="label"
        sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', p: 1.5, cursor: 'pointer' }}
      >
        <Box
          component="input"
          type="radio"
          name="outcome"
          value={value}
          checked={checked}
          onChange={() => onChange(value)}
          aria-describedby={`outcome-effect-${value}`}
          sx={{ mt: 0.4, accentColor: palette.brand.accent, width: 16, height: 16, flexShrink: 0 }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography
            id={`outcome-effect-${value}`}
            variant="body2"
            sx={{ color: palette.text.secondary }}
          >
            {effect}
          </Typography>
        </Box>
      </Box>
      {checked && children && <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>{children}</Box>}
    </Box>
  );
}

/**
 * The "log a contact" flow, in a side drawer (full screen on a phone), ordered
 * the way the conversation goes: how they reached us, who, about what, the
 * topic, and what came of it.
 */
function LogContactDrawer({
  open,
  onClose,
  localGovernmentId,
}: {
  open: boolean;
  onClose: () => void;
  localGovernmentId: string;
}) {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { user } = useAuth();
  const create = useCreateContactEntry();
  const assignees = useContactAssignees(localGovernmentId);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [tried, setTried] = useState(false);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const close = () => {
    onClose();
    setTried(false);
  };

  const wantsCallback = form.outcome === 'callback';
  const dueIso = localInputToIso(form.callbackDue);
  const outcomeMissing = !form.outcome;
  const callbackIncomplete = wantsCallback && !(form.callbackAssigneeId && dueIso);
  const canSubmit = !create.isPending && !outcomeMissing && !callbackIncomplete;
  const assigneeName =
    form.callbackAssigneeId === user?.id
      ? t('gov.frontDesk.outcomeEffect.you')
      : assignees.data?.find((a) => a.id === form.callbackAssigneeId)?.displayName;

  const pickOutcome = (outcome: PickableContactOutcome) =>
    setForm((f) => ({
      ...f,
      outcome,
      // Default the callback to the clerk taking the call.
      callbackAssigneeId:
        outcome === 'callback' && !f.callbackAssigneeId ? (user?.id ?? '') : f.callbackAssigneeId,
    }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (!canSubmit || !form.outcome) return;
    const trim = (v: string) => v.trim() || undefined;
    const dto: CreateContactLogDto = {
      localGovernmentId,
      channel: form.channel,
      anonymous: form.anonymous,
      callerName: trim(form.callerName),
      callerPhone: trim(form.callerPhone),
      callerEmail: trim(form.callerEmail),
      address: trim(form.address),
      pid: trim(form.pid),
      ownerName: trim(form.ownerName),
      topic: form.topic,
      notes: trim(form.notes),
      outcome: form.outcome,
      fileIds: form.files.map((f) => f.id),
      ...(wantsCallback && dueIso
        ? { callbackAssigneeId: form.callbackAssigneeId, callbackDueAt: dueIso }
        : {}),
    };
    create.mutate(dto, {
      onSuccess: () => {
        snackbar.success(t('gov.frontDesk.form.saved'));
        setForm(emptyForm());
        close();
      },
      onError: () => snackbar.error(t('gov.frontDesk.form.saveFailed')),
    });
  };

  const effect = (o: PickableContactOutcome) =>
    o === 'callback'
      ? assigneeName
        ? t('gov.frontDesk.outcomeEffect.callbackTo', { name: assigneeName })
        : t('gov.frontDesk.outcomeEffect.callback')
      : t(`gov.frontDesk.outcomeEffect.${o}`);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={close}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', sm: 560 },
            maxWidth: '100%',
            backgroundColor: palette.background.default,
          },
        },
      }}
    >
      <Box
        component="form"
        onSubmit={submit}
        noValidate
        aria-label={t('gov.frontDesk.form.title')}
        sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1,
            px: { xs: 2, sm: 3 },
            height: 64,
            flexShrink: 0,
            backgroundColor: palette.background.paper,
            borderBottom: `1px solid ${palette.border.line}`,
          }}
        >
          <Typography
            component="h2"
            sx={{ flex: 1, fontFamily: fontStacks.display, fontWeight: 700, fontSize: 18 }}
          >
            {t('gov.frontDesk.form.title')}
          </Typography>
          <IconButton onClick={close} aria-label={t('common.actions.close')}>
            <Icons.X size={iconSize.lg} />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, sm: 3 }, ...quietScrollbar(false) }}>
          <Step n={1} title={t('gov.frontDesk.form.channel')}>
            <ToggleButtonGroup
              exclusive
              size="small"
              fullWidth
              value={form.channel}
              onChange={(_e, v: ContactChannel | null) => v && set('channel', v)}
              aria-label={t('gov.frontDesk.form.channel')}
              sx={{ backgroundColor: palette.background.paper }}
            >
              {CONTACT_CHANNELS.map((c) => {
                const Icon = CHANNEL_ICON[c];
                return (
                  <ToggleButton key={c} value={c} sx={{ gap: 0.75, px: 1.5 }}>
                    <Icon size={iconSize.md} aria-hidden />
                    {t(`gov.frontDesk.channel.${c}`)}
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>
          </Step>

          <Step n={2} title={t('gov.frontDesk.form.who')}>
            <Stack spacing={2}>
              <TextField
                size="small"
                fullWidth
                label={t('gov.frontDesk.form.callerName')}
                value={form.callerName}
                onChange={(e) => set('callerName', e.target.value)}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  size="small"
                  fullWidth
                  type="tel"
                  label={t('gov.frontDesk.form.callerPhone')}
                  value={form.callerPhone}
                  onChange={(e) => set('callerPhone', e.target.value)}
                />
                <TextField
                  size="small"
                  fullWidth
                  type="email"
                  label={t('gov.frontDesk.form.callerEmail')}
                  value={form.callerEmail}
                  onChange={(e) => set('callerEmail', e.target.value)}
                />
              </Stack>
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.anonymous}
                      onChange={(e) => set('anonymous', e.target.checked)}
                    />
                  }
                  label={t('gov.frontDesk.form.anonymous')}
                />
                {form.anonymous && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {t('gov.frontDesk.form.anonymousHint')}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Step>

          <Step n={3} title={t('gov.frontDesk.form.aboutWhat')}>
            <Stack spacing={2}>
              <TextField
                size="small"
                fullWidth
                label={t('gov.frontDesk.form.address')}
                value={form.address}
                onChange={(e) => set('address', e.target.value)}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  size="small"
                  fullWidth
                  label={t('gov.frontDesk.form.pid')}
                  value={form.pid}
                  onChange={(e) => set('pid', e.target.value)}
                />
                <TextField
                  size="small"
                  fullWidth
                  label={t('gov.frontDesk.form.ownerName')}
                  value={form.ownerName}
                  onChange={(e) => set('ownerName', e.target.value)}
                />
              </Stack>
              <Box>
                <FileLinkPicker
                  localGovernmentId={localGovernmentId}
                  value={form.files}
                  onChange={(files) => set('files', files)}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  {t('gov.frontDesk.form.linkedFilesHint')}
                </Typography>
              </Box>
            </Stack>
          </Step>

          <Step n={4} title={t('gov.frontDesk.form.topic')}>
            <Stack spacing={2}>
              <TextField
                select
                size="small"
                fullWidth
                label={t('gov.frontDesk.form.topic')}
                value={form.topic}
                onChange={(e) => set('topic', e.target.value as ContactTopic)}
              >
                {CONTACT_TOPICS.map((topic) => (
                  <MenuItem key={topic} value={topic}>
                    {t(`gov.frontDesk.topic.${topic}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                multiline
                minRows={3}
                fullWidth
                label={t('gov.frontDesk.form.notes')}
                value={form.notes}
                onChange={(e) => set('notes', e.target.value.slice(0, CONTACT_NOTES_MAX))}
              />
            </Stack>
          </Step>

          <Step n={5} title={t('gov.frontDesk.form.outcome')}>
            <Box
              component="fieldset"
              aria-describedby="outcome-hint"
              sx={{ border: 'none', m: 0, p: 0, minWidth: 0 }}
            >
              <Box component="legend" sx={{ ...visuallyHidden }}>
                {t('gov.frontDesk.form.outcome')}
              </Box>
              <Stack spacing={1}>
                {PICKABLE_CONTACT_OUTCOMES.map((o) => (
                  <OutcomeOption
                    key={o}
                    value={o}
                    checked={form.outcome === o}
                    onChange={pickOutcome}
                    title={t(`gov.frontDesk.outcome.${o}`)}
                    effect={effect(o)}
                  >
                    {o === 'callback' && (
                      <CallbackFields
                        localGovernmentId={localGovernmentId}
                        meId={user?.id ?? null}
                        assigneeId={form.callbackAssigneeId}
                        dueLocal={form.callbackDue}
                        onAssignee={(id) => set('callbackAssigneeId', id)}
                        onDue={(v) => set('callbackDue', v)}
                      />
                    )}
                  </OutcomeOption>
                ))}
              </Stack>
              {tried && outcomeMissing && (
                <Typography
                  role="alert"
                  variant="body2"
                  sx={{ mt: 1, color: palette.semantic.emergency, fontWeight: 600 }}
                >
                  {t('gov.frontDesk.form.outcomeRequired')}
                </Typography>
              )}
              <Typography
                id="outcome-hint"
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1.5 }}
              >
                {t('gov.frontDesk.form.laterActions')}
              </Typography>
            </Box>
          </Step>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: 'flex-end',
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            py: 1.5,
            backgroundColor: palette.background.paper,
            borderTop: `1px solid ${palette.border.line}`,
          }}
        >
          <Button type="button" onClick={close} disabled={create.isPending}>
            {t('gov.frontDesk.cancel')}
          </Button>
          {/* Enabled even before an outcome is picked: pressing it then says
              what is missing, where a disabled button says nothing. */}
          <Button type="submit" variant="contained" disabled={create.isPending}>
            {t('gov.frontDesk.form.submit')}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}

/** Attach an entry to an existing file's history. */
function AttachDialog({ entry, onClose }: { entry: ContactLogEntryView; onClose: () => void }) {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const attach = useAttachContact();
  const [files, setFiles] = useState<ContactLinkedFile[]>([]);
  const file = files[0];
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.frontDesk.attach.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <FileLinkPicker
            multiple={false}
            localGovernmentId={entry.localGovernmentId}
            value={files}
            onChange={setFiles}
            label={t('gov.frontDesk.attach.file')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('gov.frontDesk.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!file || attach.isPending}
          onClick={() =>
            file &&
            attach.mutate(
              { id: entry.id, fileId: file.id },
              {
                onSuccess: () => {
                  snackbar.success(t('gov.frontDesk.attach.done', { reference: file.reference }));
                  onClose();
                },
                onError: () => snackbar.error(t('gov.frontDesk.actionFailed')),
              },
            )
          }
        >
          {t('gov.frontDesk.attach.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Hand an entry to the public-report triage queue. */
function TriageDialog({ entry, onClose }: { entry: ContactLogEntryView; onClose: () => void }) {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const triage = useSendContactToTriage();
  const [description, setDescription] = useState(entry.notes ?? '');
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.frontDesk.triage.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('gov.frontDesk.triage.body')}
        </Typography>
        {!entry.address && (
          <Alert severity="warning">{t('gov.frontDesk.triage.needsAddress')}</Alert>
        )}
        <TextField
          multiline
          minRows={3}
          fullWidth
          sx={{ mt: 1 }}
          label={t('gov.frontDesk.triage.description')}
          value={description}
          onChange={(e) => setDescription(e.target.value.slice(0, CONTACT_NOTES_MAX))}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('gov.frontDesk.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!entry.address || !description.trim() || triage.isPending}
          onClick={() =>
            triage.mutate(
              { id: entry.id, description: description.trim() },
              {
                onSuccess: () => {
                  snackbar.success(t('gov.frontDesk.triage.done'));
                  onClose();
                },
                onError: () => snackbar.error(t('gov.frontDesk.actionFailed')),
              },
            )
          }
        >
          {t('gov.frontDesk.triage.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Set or reassign the callback on an entry. */
function CallbackDialog({ entry, onClose }: { entry: ContactLogEntryView; onClose: () => void }) {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { user } = useAuth();
  const setCallback = useSetContactCallback();
  const [assigneeId, setAssigneeId] = useState(entry.callback?.assigneeId ?? user?.id ?? '');
  const [due, setDue] = useState(defaultCallbackDue());
  const dueIso = localInputToIso(due);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.frontDesk.callback.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <CallbackFields
            localGovernmentId={entry.localGovernmentId}
            meId={user?.id ?? null}
            assigneeId={assigneeId}
            dueLocal={due}
            onAssignee={setAssigneeId}
            onDue={setDue}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('gov.frontDesk.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!assigneeId || !dueIso || setCallback.isPending}
          onClick={() =>
            dueIso &&
            setCallback.mutate(
              { id: entry.id, assigneeId, dueAt: dueIso },
              {
                onSuccess: () => {
                  snackbar.success(t('gov.frontDesk.callback.saved'));
                  onClose();
                },
                onError: () => snackbar.error(t('gov.frontDesk.actionFailed')),
              },
            )
          }
        >
          {t('gov.frontDesk.callback.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type Dialogs = { kind: 'attach' | 'triage' | 'callback'; entry: ContactLogEntryView } | null;

/**
 * The front desk: the recent contacts, searchable, with "Log a contact" opening
 * the short logging flow in a drawer. A logged contact is then turned into a
 * new file, a line on an existing file, a triage report or a callback from its
 * row here.
 */
export function FrontDesk() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const status = useFrontDeskStatus();
  const lgs = status.data?.localGovernments ?? [];
  const [chosenLg, setChosenLg] = useState<string | null>(null);
  const localGovernmentId = chosenLg ?? lgs[0]?.id ?? null;
  const [q, setQ] = useState('');
  const entries = useContactEntries(localGovernmentId, q, Boolean(status.data?.enabled));
  const [dialog, setDialog] = useState<Dialogs>(null);
  const [logging, setLogging] = useState(false);
  const enabled = status.data?.enabled && localGovernmentId;

  const logButton = (
    <Button
      variant="contained"
      startIcon={<Icons.Plus size={iconSize.md} />}
      onClick={() => setLogging(true)}
      sx={{ flexShrink: 0, height: 40 }}
    >
      {t('gov.frontDesk.form.title')}
    </Button>
  );

  return (
    <AppShell>
      <PageIntro>{t('gov.frontDesk.subtitle')}</PageIntro>

      {status.isLoading && <CircularProgress size={24} />}
      {status.isError && <Alert severity="error">{t('gov.frontDesk.loadFailed')}</Alert>}
      {status.data && !status.data.enabled && (
        <Alert severity="info">{t('gov.frontDesk.disabled')}</Alert>
      )}

      {enabled && (
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            sx={{ gap: 1.5, alignItems: { sm: 'center' } }}
          >
            {lgs.length > 1 && (
              <TextField
                select
                size="small"
                sx={{ width: { xs: '100%', sm: 220 } }}
                label={t('gov.frontDesk.municipality')}
                value={localGovernmentId}
                onChange={(e) => setChosenLg(e.target.value)}
              >
                {lgs.map((lg) => (
                  <MenuItem key={lg.id} value={lg.id}>
                    {lg.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              size="small"
              type="search"
              placeholder={t('gov.frontDesk.recent.search')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              sx={{
                width: { xs: '100%', sm: 320 },
                // The same height as the button beside it: a 56px field next to
                // a 40px button was the misaligned box the desk complained of.
                '& .MuiInputBase-root': {
                  height: 40,
                  minHeight: 40,
                  backgroundColor: palette.background.paper,
                },
              }}
              slotProps={{
                htmlInput: { 'aria-label': t('gov.frontDesk.recent.search') },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icons.Search size={iconSize.sm} aria-hidden />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
            {logButton}
          </Stack>

          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700, pt: 1 }}>
            {t('gov.frontDesk.recent.title')}
          </Typography>

          {entries.isLoading && (
            <Box sx={{ p: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {entries.data && entries.data.items.length === 0 && (
            <HairlineCard sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
              <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
                <Box
                  aria-hidden
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: palette.brand.accentTint,
                    color: palette.brand.accent,
                  }}
                >
                  <Icons.Phone size={iconSize.lg} />
                </Box>
                <Typography sx={{ fontWeight: 700 }}>
                  {q.trim() ? t('gov.frontDesk.recent.noMatch') : t('gov.frontDesk.recent.empty')}
                </Typography>
                {!q.trim() && (
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '52ch' }}>
                    {t('gov.frontDesk.recent.emptyBody')}
                  </Typography>
                )}
              </Stack>
            </HairlineCard>
          )}
          {entries.data && entries.data.items.length > 0 && (
            <HairlineCard sx={{ p: 0 }}>
              {entries.data.items.map((entry) => {
                const done = entry.outcome === 'file_opened' || entry.outcome === 'sent_to_triage';
                return (
                  <ContactEntrySummary
                    key={entry.id}
                    entry={entry}
                    highlight={entry.callback?.overdue}
                    actions={
                      entry.canEdit && (
                        <>
                          {!done && (
                            <Button
                              size="small"
                              onClick={() => navigate(intakeHrefForContact(entry.id))}
                            >
                              {t('gov.frontDesk.actions.openFile')}
                            </Button>
                          )}
                          <Button size="small" onClick={() => setDialog({ kind: 'attach', entry })}>
                            {t('gov.frontDesk.actions.attach')}
                          </Button>
                          {!done && !entry.triageComplaintId && (
                            <Button
                              size="small"
                              onClick={() => setDialog({ kind: 'triage', entry })}
                            >
                              {t('gov.frontDesk.actions.triage')}
                            </Button>
                          )}
                          {!entry.callback?.doneAt && (
                            <Button
                              size="small"
                              onClick={() => setDialog({ kind: 'callback', entry })}
                            >
                              {entry.callback
                                ? t('gov.frontDesk.actions.reassignCallback')
                                : t('gov.frontDesk.actions.callback')}
                            </Button>
                          )}
                        </>
                      )
                    }
                  />
                );
              })}
            </HairlineCard>
          )}

          <LogContactDrawer
            key={localGovernmentId}
            open={logging}
            onClose={() => setLogging(false)}
            localGovernmentId={localGovernmentId}
          />
        </Stack>
      )}

      {dialog?.kind === 'attach' && (
        <AttachDialog entry={dialog.entry} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'triage' && (
        <TriageDialog entry={dialog.entry} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'callback' && (
        <CallbackDialog entry={dialog.entry} onClose={() => setDialog(null)} />
      )}
    </AppShell>
  );
}
